import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { EncodeMeshGeometryToDraco, EncodePointsArrayToDraco } from '@/engine/Utilities/DracoUtils';

import { Orientation } from './DFVD/orientation';
import { Mesh } from './DFVD/mesh';
import { Polyline } from './DFVD/polyline';
import { Vector3 } from './DFVD/vector3';
import { Vector } from './DFVD/vector';
import { Line } from './DFVD/line';
import { Plane } from './DFVD/plane';
import { DataFlowVertexData } from './DFVD/data-flow-vertex-data';

import * as THREE from 'three';
import * as flatbuffers from 'flatbuffers';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { IExporter } from './IExporter';
import { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { sha256 } from '@/engine/utils/hash';

export class DFVDExporter implements IExporter {
  private readonly _algo: PlanningAlgorithm;

  private readonly _vertices: Array<string>;

  public constructor(algo: PlanningAlgorithm, vertices: Array<string>) {
    this._algo = algo;
    this._vertices = vertices;
  }

  public async export(): Promise<Blob> {
    const zip = new JSZip();
    const builder = new flatbuffers.Builder(1024);
    const meshes: Array<number> = [];
    const polylines: Array<number> = [];
    const points: Array<number> = [];
    const lines: Array<number> = [];
    const planes: Array<number> = [];
    for (const name of this._vertices) {
      const type = this._algo.getType(name);
      if (type == DT.Mesh.Type) {
        const mesh = this._algo.getValue(name, DT.Mesh);
        if (mesh) {
          const descriptor = await sha256(name);
          if (mesh.value.attributes.position?.array.length) {
            zip.file(
              descriptor + '.drc',
              new Blob([EncodeMeshGeometryToDraco(mesh.value)], {
                type: 'octet/stream'
              }),
              { base64: false, binary: true }
            );
          }
          const c = new THREE.Vector3(0, 0, 0);
          const y = new THREE.Vector3(0, 1, 0);
          const z = new THREE.Vector3(0, 0, 1);
          if (this._algo.has(name + '.cs')) {
            const cs = this._algo.getValue(name + '.cs', DT.TransformMatrix);
            if (cs) {
              c.applyMatrix4(cs.value);
              y.transformDirection(cs.value);
              z.transformDirection(cs.value);
            }
          }
          const n = builder.createString(name);
          const d = builder.createString(descriptor);
          Mesh.startMesh(builder);
          Mesh.addName(builder, n);
          Mesh.addDescriptor(builder, d);
          Mesh.addOrientation(
            builder,
            Orientation.createOrientation(builder, c.x, c.y, c.z, y.x, y.y, y.z, z.x, z.y, z.z)
          );
          meshes.push(Mesh.endMesh(builder));
        }
      } else if (type == DT.Polyline.Type) {
        const polyline = this._algo.getValue(name, DT.Polyline);
        if (polyline) {
          const descriptor = await sha256(name);
          if (polyline.value.points.length) {
            zip.file(
              descriptor + '.drc',
              new Blob([EncodePointsArrayToDraco(polyline.value.points)], {
                type: 'octet/stream'
              }),
              { base64: false, binary: true }
            );
          }
          const n = builder.createString(name);
          const d = builder.createString(descriptor);
          Polyline.startPolyline(builder);
          Polyline.addName(builder, n);
          Polyline.addDescriptor(builder, d);
          Polyline.addIsClosed(builder, polyline.value.isClosed);
          polylines.push(Polyline.endPolyline(builder));
        }
      } else if (type == DT.Point.Type) {
        const point = this._algo.getValue(name, DT.Point);
        if (point) {
          const n = builder.createString(name);
          Vector.startVector(builder);
          Vector.addName(builder, n);
          Vector.addCoords(builder, Vector3.createVector3(builder, point.value.x, point.value.y, point.value.z));
          points.push(Vector.endVector(builder));
        }
      } else if (type == DT.Line.Type) {
        const line = this._algo.getValue(name, DT.Line);
        if (line) {
          const n = builder.createString(name);
          Line.startLine(builder);
          Line.addName(builder, n);
          Line.addFrom(
            builder,
            Vector3.createVector3(builder, line.value.start.x, line.value.start.y, line.value.start.z)
          );
          Line.addTo(builder, Vector3.createVector3(builder, line.value.end.x, line.value.end.y, line.value.end.z));
          lines.push(Line.endLine(builder));
        }
      } else if (type == DT.Plane.Type) {
        const plane = this._algo.getValue(name, DT.Plane);
        if (plane) {
          const n = builder.createString(name);
          Plane.startPlane(builder);
          Plane.addName(builder, n);
          Plane.addOrigin(
            builder,
            Vector3.createVector3(builder, plane.value.origin.x, plane.value.origin.y, plane.value.origin.z)
          );
          Plane.addNormal(
            builder,
            Vector3.createVector3(
              builder,
              plane.value.planeEq.normal.x,
              plane.value.planeEq.normal.y,
              plane.value.planeEq.normal.z
            )
          );
          planes.push(Plane.endPlane(builder));
        }
      }
    }
    const mds = DataFlowVertexData.createModelsVector(builder, meshes);
    const pol = DataFlowVertexData.createPolylinesVector(builder, polylines);
    const pts = DataFlowVertexData.createPointsVector(builder, points);
    const lns = DataFlowVertexData.createLinesVector(builder, lines);
    const pls = DataFlowVertexData.createPlanesVector(builder, planes);
    DataFlowVertexData.startDataFlowVertexData(builder);
    DataFlowVertexData.addModels(builder, mds);
    DataFlowVertexData.addPolylines(builder, pol);
    DataFlowVertexData.addPoints(builder, pts);
    DataFlowVertexData.addLines(builder, lns);
    DataFlowVertexData.addPlanes(builder, pls);
    const dfvd = DataFlowVertexData.endDataFlowVertexData(builder);
    builder.finish(dfvd);
    const header = builder.asUint8Array();
    zip.file('header.dfvd', header, { base64: false, binary: true });
    return zip.generateAsync({ type: 'blob' });
  }

  public async download(): Promise<void> {
    const zip = await this.export();
    FileSaver.saveAs(zip, 'Data.zip');
  }
}
